import { ArrowLeft, CalendarClock, FileText, Pause, Sparkles, UserRound } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api, formatMoney, type Blocker, type CommitmentView, type CustomerDeskView } from '../api';
import { DeskStates } from '../components/blocked-chip';
import { ActNow } from '../components/act-now';
import { AgingBar, DeskSection, EmptyNote, MoneyLines, ago, formatDay, formatMoment, useDeskData } from '../components/desk';
import { AskAmy, CustomerTimeline, PaymentHabit, safeHref, type Brief } from '../components/customer-360';
import { CaseStory } from '../components/case-story';
import { CustomerFileCard } from '../components/customer-file';
import { StuckStrip } from '../components/stuck-strip';
import { Button } from '../components/ui/button';
import { Kbd } from '../components/ui/kbd';
import { askShortcutLabel } from '../amy/shortcut';
import '../customer-360.css';

type Step = CustomerDeskView['nextStep'];
const statusLabels: Record<string, string> = {
  open: 'Open', overdue: 'Overdue', paused: 'Paused', promised: 'Promised', disputed: 'Disputed', broken_promise: 'Broken promise',
  wrong_contact: 'Wrong contact', out_of_office: 'Out of office', unknown: 'Balance unknown', paid: 'Paid', void: 'Void',
  written_off: 'Written off', draft: 'Draft',
};
const stepStatus: Record<string, string> = {
  due_today: 'Due today', next_window: 'At the customer’s next send window', awaiting_approval: 'Waiting for your approval',
  planned: 'Planned', paused: 'Paused', none: 'Nothing planned',
  ready: 'Approved, ready to send', undo_wait: 'Approved, Undo window open', snoozed: 'Snoozed',
};

function NextStep({ step, persona, asOf }: { step: Step; persona: string; asOf: string }) {
  return <section className={`next-step ${step.status}`} aria-label={`${persona}’s next step`}>
    <header><span className="next-step-mark">
      {step.status === 'paused' ? <Pause size={17} aria-hidden /> : <Sparkles size={17} aria-hidden />}</span>
      <div><h2>{persona}’s next step</h2><span>{stepStatus[step.status] ?? step.status}</span></div>
      {step.date && <time dateTime={step.date}><CalendarClock size={14} aria-hidden />
        {step.date === asOf ? 'Today' : formatDay(step.date, asOf)}</time>}
    </header>
    <strong className="next-step-title">{step.summary}</strong>
    <p>{step.description.replace(/\.?$/, '.')}
      {step.invoiceNumbers.length > 0 && <> Covers {step.invoiceNumbers.join(', ')}.</>}</p>
    {step.why.length > 0 && <><h3>Why</h3><ul>{step.why.map(w => <li key={w}>{w}</li>)}</ul></>}
    <footer>
      <span>From {step.policy.name}, version {step.policy.version.replace(/^.*-v/, 'v')}</span>
      {step.approvalId && <a href={`#approvals/${step.approvalId}`}>Open the draft</a>}
    </footer>
    {step.assumption && <small className="next-step-note">{step.assumption}</small>}
  </section>;
}

const commitmentStatus: Record<string, string> = { open: 'Owed', done: 'Done', failed: 'Not kept', cancelled: 'Replaced' };
/** What the agent and the team promised this customer (#215): open ones first, each with its owner, due time and status. */
function Commitments({ items, persona, asOf }: { items: CommitmentView[]; persona: string; asOf: string }) {
  if (!items.length) return null;
  const open = items.filter(c => c.status === 'open').length;
  return <DeskSection title="Promised" aside={<small className="muted">{open ? `${open} owed` : 'All kept'}</small>}>
    <ul className="commitment-list">{items.map(c => <li key={c.id} className={`commitment ${c.status}${c.overdue ? ' late' : ''}`}>
      <p>{c.text}</p>
      <small>{c.owner === 'carol' ? persona : 'The team'} · {commitmentStatus[c.status] ?? c.status}
        {c.status === 'open' && (c.overdue ? ' · past due' : ` · due ${formatMoment(c.dueAt, asOf)}`)}
        {c.needsYouTaskId ? ' · on Needs you' : ''}</small>
      {c.status === 'open' && c.problem && <small className="commitment-problem">{c.problem}</small>}
    </li>)}</ul>
  </DeskSection>;
}

/** The brief arrives with the desk when Amy has already written it for these facts; otherwise it is written now. */
function useBrief(desk: CustomerDeskView | null) {
  const [written, setWritten] = useState<Brief | null>(null); const [error, setError] = useState('');
  const key = desk?.briefKey; const cached = desk?.brief ?? null; const id = desk?.customer.id;
  useEffect(() => {
    if (!key || !id || cached) return;
    let active = true; setError('');
    void api<Brief>(`/app-api/customers/${encodeURIComponent(id)}/brief`).then(b => { if (active) setWritten(b); })
      .catch(() => { if (active) setError('The summary could not load. The figures below are current.'); });
    return () => { active = false; };
  }, [key, id, cached]);
  if (cached) return { brief: cached as Brief, error: '' };
  return { brief: written && written.key === key ? written : null, error };
}

/** The channel chooser's latest decision for this customer (#285): which channel, why, and whether a call waits for tomorrow. */
interface ChannelChoiceView { localDate: string; channel: string; rule: string; reason: string; deferred: boolean; expectedCostCents: number }
function ChannelChoiceLine({ choice }: { choice: ChannelChoiceView | null }) {
  if (!choice) return null;
  return <p className="desk-footnote" aria-label="Channel choice">
    <strong>{choice.deferred ? 'Call deferred' : choice.channel === 'call' ? 'Calling' : choice.channel === 'email' ? 'Email first' : 'No contact'}</strong>
    {' · '}{choice.reason} <span className="muted">({choice.localDate})</span>
  </p>;
}

export function CustomerDesk({ customerId, owner = false, onAsk }: {
  customerId: string; owner?: boolean; onAsk?: () => void;
}) {
  const { data: desk, error } = useDeskData<CustomerDeskView>(`/app-api/customers/${encodeURIComponent(customerId)}/desk`);
  const brief = useBrief(desk);
  const invoiceIds = useMemo(() => new Set(desk?.invoices.map(i => i.id) ?? []), [desk]);
  const blockers = (desk as CustomerDeskView & { blockers?: Blocker[] } | null)?.blockers ?? [];
  return <main id="main-content" className="queue-page desk-page customer-desk">
    {error && <div className="alert error" role="alert">{error}</div>}
    {!desk && !error && <p className="muted" role="status">Opening the customer desk…</p>}
    {desk && <>
      <header className="desk-sticky">
        <div className="desk-sticky-bar">
          <div className="desk-sticky-identity">
            <a className="desk-back" href="#customers"><ArrowLeft size={14} aria-hidden />All customers</a>
            <h1>{desk.customer.name}</h1>
            <p className="desk-meta">
              <span>{desk.customer.timezone.split('/').pop()!.replace(/_/g, ' ')} time</span>
              <span>{desk.customer.policy.name}</span>
              <span className={`habit ${desk.behavior.hint.kind}`}>{desk.behavior.hint.label}</span>
            </p>
            <DeskStates badges={desk.badges} status={desk.customer.status} blockers={blockers} />
          </div>
          <dl className="desk-figures desk-sticky-figures">
            <div><dt>Open</dt><dd><MoneyLines values={desk.balances} empty="Settled" /></dd></div>
            <div><dt>Overdue</dt><dd><MoneyLines values={desk.overdue} empty="—" /></dd></div>
            <div><dt>Oldest</dt><dd>{desk.oldestDaysOverdue ? `${desk.oldestDaysOverdue}d` : '–'}</dd></div>
            <div><dt>Last contact</dt><dd>{desk.lastContact ? ago(desk.lastContact.at, desk.asOf) : 'None'}</dd></div>
          </dl>
          <div className="desk-sticky-actions">
            {onAsk && <Button type="button" variant="outline" size="sm" className="desk-ask-btn" onClick={onAsk}>
              <Sparkles size={14} aria-hidden />Ask {desk.persona}<Kbd>{askShortcutLabel}</Kbd>
            </Button>}
            {owner && <ActNow customerId={desk.customer.id} />}
          </div>
        </div>
        <StuckStrip blockers={blockers} className="desk-stuck" />
      </header>
      {!desk.balanceComplete && <p className="desk-footnote">Some open balances are unknown at the source and are not in these totals.</p>}
      <ChannelChoiceLine choice={(desk as CustomerDeskView & { channelChoice?: ChannelChoiceView | null }).channelChoice ?? null} />
      <CaseStory desk={desk} brief={brief.brief} briefError={brief.error} />
      <div className="desk-grid">
        <div className="desk-main">
          <NextStep step={desk.nextStep} persona={desk.persona} asOf={desk.asOf} />
          <PaymentHabit behavior={desk.behavior} />
          {desk.aging.some(a => Object.values(a.buckets).some(v => v !== '0')) && <DeskSection title="Aging">
            {desk.aging.map(a => <AgingBar key={a.currency} currency={a.currency}
              buckets={Object.entries(a.buckets).map(([bucket, minor]) => ({ bucket, minor }))} />)}</DeskSection>}
          <DeskSection title="Invoices" aside={<small className="muted">{desk.invoices.length} on record</small>}
            className="desk-invoices">
            {desk.invoices.length ? <div className="desk-table-wrap"><table className="desk-table desk-table-dense">
              <thead><tr><th>Number</th><th>Issued</th><th>Due</th><th className="numeric">Amount</th><th className="numeric">Balance</th>
                <th>Status</th><th className="numeric">Days overdue</th></tr></thead>
              <tbody>{desk.invoices.map(i => { const pdf = safeHref(i.pdfUrl);
                return <tr key={i.id} id={`invoice-row-${i.id}`}
                  className={['paid', 'void', 'written_off'].includes(i.status) ? 'settled' : ''}>
                  <td>{pdf ? <a href={pdf} target="_blank" rel="noreferrer"><FileText size={12} aria-hidden />{i.number}</a>
                    : <span tabIndex={-1}>{i.number}</span>}</td>
                  <td>{formatDay(i.issuedOn, desk.asOf)}</td><td>{formatDay(i.dueOn, desk.asOf)}</td>
                  <td className="numeric">{formatMoney(i.amount.minor, i.amount.currency)}</td>
                  <td className="numeric">{i.balance ? formatMoney(i.balance.minor, i.balance.currency) : 'Unknown'}</td>
                  <td><span className={`invoice-status ${i.status}`}>{statusLabels[i.status] ?? i.status}</span></td>
                  <td className="numeric">{i.daysOverdue || <span className="muted">–</span>}</td></tr>; })}</tbody>
            </table></div> : <EmptyNote icon={<FileText size={22} aria-hidden />} title="No invoices for this customer" />}
          </DeskSection>
        </div>
        <aside className="desk-side">
          <AskAmy customerId={desk.customer.id} customerName={desk.customer.name} persona={desk.persona} invoiceIds={invoiceIds} />
          <Commitments items={(desk as CustomerDeskView & { commitments?: CommitmentView[] }).commitments ?? []}
            persona={desk.persona} asOf={desk.asOf} />
          <CustomerFileCard customerId={desk.customer.id} persona={desk.persona} asOf={desk.asOf} />
          <CustomerTimeline events={desk.timeline} asOf={desk.asOf} persona={desk.persona} />
          <DeskSection title="Contacts">
            {desk.contacts.length ? <ul className="contact-list">{desk.contacts.map(c => <li key={c.id}><UserRound size={15} aria-hidden />
              <div><strong>{c.name}</strong><a href={`mailto:${c.email}`}>{c.email}</a>
                <small>{c.role === 'ap' ? 'Accounts payable' : c.role[0]!.toUpperCase() + c.role.slice(1)}
                  {c.status !== 'active' && <span className="desk-badge paused">{c.status}</span>}</small>
                {c.addedNote && <small className="muted">{c.addedNote}</small>}
                {owner && c.addedBy && c.status === 'active' && <Button variant="ghost" size="sm" aria-label={`Remove ${c.name}`}
                  onClick={() => void api(`/app-api/contacts/${encodeURIComponent(c.id)}/remove`, {}).catch(() => undefined)}>Remove</Button>}</div></li>)}</ul>
              : <EmptyNote icon={<UserRound size={20} aria-hidden />} title="No contacts">
                Reminders wait until a billing contact exists.</EmptyNote>}
          </DeskSection>
        </aside>
      </div>
    </>}
  </main>;
}
